import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@zones-digital-platform/react-oidc-auth";
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Drawer,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Paper,
  Alert,
  Snackbar,
  CircularProgress,
  Container,
  Tabs,
  Tab,
  Stack,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Logout as LogoutIcon,
  Home as HomeIcon,
  Search as SearchIcon,
  Upload as UploadIcon,
  Download as DownloadIcon,
} from "@mui/icons-material";
import {
  Inventory2 as InventoryIcon,
  Menu as MenuIcon,
} from "@mui/icons-material";
import { DataGrid } from "@mui/x-data-grid";
import "../css/spinner.css";
import { saveAs } from "file-saver";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import { CssBaseline } from "@mui/material";
import * as XLSX from "xlsx";

const theme = createTheme({
  typography: {
    fontSize: 16, // default is 14 → bump it to match Tailwind’s base (1rem = 16px)
  },
});
const APP_BAR_HEIGHT = 64;
const PartnerInventory = () => {
  const [partnerInventories, setPartnerInventories] = useState([]);
  const [stockRooms, setStockRooms] = useState([]);
  const [deviceModels, setDeviceModels] = useState([]);
  const [keyboardTypes, setKeyboardTypes] = useState([]);
  const [customerAccounts, setCustomerAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [searchQuery, setSearchQuery] = useState("");
  const [formTab, setFormTab] = useState(0); // 0 for individual, 1 for upload
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadLoading, setUploadLoading] = useState(false);
  const [navOpen, setNavOpen] = useState(false);
  const [formData, setFormData] = useState({
    customerAccountId: "",
    stockRoom: "",
    leaseRefNumber: "",
    serviceNowTicket: "",
    serialNumber: "",
    keyboardType: "",
    deviceModel: "",
  });
  const [editingId, setEditingId] = useState(null);
  const { isAuthenticated, logout, getToken } = useAuth();
const [totalCount, setTotalCount] = useState(0);
const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10,
  });

  // DataGrid columns configuration
  const columns = [
    // { field: 'id', headerName: 'ID', width: 80 },
    {
      field: "customer",
      headerName: "Customer",
      width: 180,
      //renderCell: (params) => customerAccountIdToLabel[params.value] || params.value,
    },
    {
      field: "partner",
      headerName: "Partner",
      width: 120,
      // renderCell: (params) => partnerIdToName[params.value] || params.value,
    },
    { field: "stockRoom", headerName: "Stockroom", width: 180 },
    { field: "leaseRefNumber", headerName: "Lease Ref Number", width: 150 },
    { field: "serviceNowTicket", headerName: "Service Now Ticket", width: 150 },
    { field: "serialNumber", headerName: "Serial Number", width: 150 },
    { field: "keyboardType", headerName: "Keyboard Type", width: 130 },
    { field: "deviceModel", headerName: "Device Model", width: 130 },
    {
      field: "createdDate",
      headerName: "Created Date",
      width: 180,
      type: "dateTime",
      valueGetter: (params) => (params ? new Date(params) : null),
    },
    {
      field: "modifiedDate",
      headerName: "Modified Date",
      width: 180,
      type: "dateTime",
      valueGetter: (params) => (params ? new Date(params) : null),
    },
    {
      field: "status",
      headerName: "Status",
      width: 120,
      renderCell: (params) => (
        <Box
          sx={{
            backgroundColor:
              params.value === "Sent"
                ? "#4caf50"
                : params.value === "Waiting"
                ? "#ff9800"
                : "#f44336",
            color: "white",
            padding: "4px 8px",
            borderRadius: "4px",
            fontWeight: "bold",
          }}
        >
          {params.value}
        </Box>
      ),
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 120,
      sortable: false,
      renderCell: (params) => (
        <Box sx={{ display: "flex", gap: 1 }}>
          <IconButton
            size="small"
            color="primary"
            onClick={() => handleEdit(params.row)}
            sx={{ backgroundColor: "#e3f2fd" }}
          >
            <EditIcon />
          </IconButton>
          <IconButton
            size="small"
            color="error"
            onClick={() => handleDelete(params.row.id)}
            sx={{ backgroundColor: "#ffebee" }}
            disabled={params.row.published === true} // Disable if published is true
          >
            <DeleteIcon />
          </IconButton>
        </Box>
      ),
    },
  ];

  useEffect(() => {
    // fetchStockRooms();
    fetchDropDowns();
    fetchUserAccounts();
  }, []);

  useEffect(() => {
    fetchPartnerInventories(paginationModel.page, paginationModel.pageSize);
  }, [paginationModel]);

  const fetchPartnerInventories = async (
  ) => {
    try {
      setLoading(true);
      const response = await fetch(
        `/api/PartnerInventory?page=${paginationModel.page + 1}&pageSize=${paginationModel.pageSize}`,
        {
          headers: {
            Authorization: `Bearer ${getToken()}`,
          },
        }
      );

      if (response.status === 401) {
        handleLogout();
      }
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      // Transform API response to match our grid structure
      const transformedData = data.data
        ? data.data.map((item) => ({
            id: item.Id,
            accountNumber: item.AccountNumber,
            customer: item.Customer,
            customerAccountId: item.CustomerAccountId,
            partnerId: item.PartnerId,
            partner: item.Partner,
            stockRoom: item.u_stockroom,
            leaseRefNumber: item.u_leasesrefnumber,
            serviceNowTicket: item.u_wservicenowticket,
            serialNumber: item.u_serialnumber,
            keyboardType: item.u_keyboard,
            deviceModel: item.u_devicemodel,
            status: item.status,
            createdDate: item.CreatedDate,
            modifiedDate: item.ModifiedDate,
            published: item.published,
          }))
        : [];

      setPartnerInventories(transformedData);
      let totalCount = 0;
      if (data.message) {
        const match = data.message?.match(/of\s+(\d+)\s+records/i);
        if (match && match[1]) {
          totalCount = parseInt(match[1], 10);
        }
      }
      setTotalCount(totalCount);
    } catch (error) {
      console.error("Error fetching partner inventories:", error);
      setSnackbar({
        open: true,
        message: "Failed to fetch partner inventories",
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchStockRooms = async () => {
    try {
      const response = await fetch("/api/PartnerInventory/StockRoom", {
        headers: {
          Authorization: `Bearer ${getToken()}`,
        },
      });
      if (response.status === 401) {
        handleLogout();
      }
      if (!response.ok) throw new Error("Failed to fetch stock rooms");
      const data = await response.json();
      setStockRooms(data);
    } catch (error) {
      console.error("Error fetching stock rooms:", error);
      setSnackbar({
        open: true,
        message: "Failed to fetch stock rooms",
        severity: "error",
      });
    }
  };

  const fetchDropDowns = async () => {
    try {
      const response = await fetch("/api/PartnerInventory/DropDowns", {
        headers: {
          Authorization: `Bearer ${getToken()}`,
        },
      });
      if (response.status === 401) {
        handleLogout();
      }
      if (!response.ok) throw new Error("Failed to fetch stock rooms");
      const data = await response.json();
      setStockRooms(data.stockRooms || []);
      setDeviceModels(data.deviceModels || []);
      setKeyboardTypes(data.keyboardTypes || []);
    } catch (error) {
      console.error("Error fetching stock rooms:", error);
      setSnackbar({
        open: true,
        message: "Failed to fetch stock rooms",
        severity: "error",
      });
    }
  };

  const fetchUserAccounts = async () => {
    try {
      const response = await fetch("/api/PartnerInventory/UserAccounts", {
        headers: {
          Authorization: `Bearer ${getToken()}`,
        },
      });
      if (response.status === 401) {
        handleLogout();
      }
      if (!response.ok) throw new Error("Failed to fetch User Accounts");
      const data = await response.json();
      // setCustomerAccounts(data);

      setCustomerAccounts(Array.isArray(data.data) ? data.data : []);
    } catch (error) {
      setSnackbar({
        open: true,
        message: "Failed to fetch User Accounts",
        severity: "error",
      });
    }
  };

  const handleLogout = async () => {
    try {
      await logout();
    } catch (error) {
      console.error("Logout error:", error);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    // Validate required fields
    const requiredFields = [
      "customerAccountId",
      "stockRoom",
      "leaseRefNumber",
      "serviceNowTicket",
      "serialNumber",
      "keyboardType",
      "deviceModel"
    ];
    for (const field of requiredFields) {
      if (!formData[field] || formData[field].toString().trim() === "") {
        setSnackbar({
          open: true,
          message: `Please fill in all required fields.`,
          severity: "error",
        });
        return;
      }
    }

    try {
      setLoading(true);
      if (editingId) {
        // Update existing item
        const response = await fetch(`/api/PartnerInventory`, {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${getToken()}`,
          },
          body: JSON.stringify({
            id: editingId,
            customerAccountId: parseInt(formData.customerAccountId),
            stockRoom: formData.stockRoom.trim(),
            leaseRefNumber: formData.leaseRefNumber.trim(),
            serviceNowTicket: formData.serviceNowTicket.trim(),
            serialNumber: formData.serialNumber.trim(),
            keyboardType: formData.keyboardType.trim(),
            deviceModel: formData.deviceModel.trim(),
            status: formData.status,
          }),
        });

        if (response.status === 401) {
          handleLogout();
        }
        if (!response.ok) {
          throw new Error("Failed to update partner inventory");
        }

        setSnackbar({
          open: true,
          message: "Partner Inventory updated successfully!",
          severity: "success",
        });
      } else {
        // Add new item
        const response = await fetch("/api/PartnerInventory", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${getToken()}`,
          },
          body: JSON.stringify({
            customerAccountId: parseInt(formData.customerAccountId),
            stockRoom: formData.stockRoom,
            leaseRefNumber: formData.leaseRefNumber.trim(),
            serviceNowTicket: formData.serviceNowTicket.trim(),
            serialNumber: formData.serialNumber.trim(),
            keyboardType: formData.keyboardType.trim(),
            deviceModel: formData.deviceModel.trim(),
          }),
        });

        if (response.status === 401) {
          handleLogout();
        }

        if (!response.ok) {
          throw new Error("Failed to create partner inventory");
        }

        if (response.ok) {
          setFormData({
            customerAccountId: "",
            stockRoom: "",
            leaseRefNumber: "",
            serviceNowTicket: "",
            serialNumber: "",
            keyboardType: "",
            deviceModel: "",
          });
        }

        setSnackbar({
          open: true,
          message: "Partner Inventory added successfully!",
          severity: "success",
        });
      }

      // Refresh the data
      await fetchPartnerInventories();

      // setEditingId(null);
      // setShowForm(false);
    } catch (error) {
      console.error("Error submitting form:", error);
      setSnackbar({
        open: true,
        message: error.message || "Failed to save partner inventory",
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleUpload = async () => {
    if (!uploadFile) {
      setSnackbar({
        open: true,
        message: "Please select a file to upload",
        severity: "warning",
      });
      return;
    }

    try {
      setUploadLoading(true);
      const fileFormData = new FormData();
      fileFormData.append("file", uploadFile);
      fileFormData.append("customerAccountId", formData.customerAccountId);

      const response = await fetch("/api/PartnerInventory-upload", {
        method: "POST",
        headers: {
          Authorization: `Bearer ${getToken()}`,
        },
        body: fileFormData,
      });

      if (response.status === 401) {
        handleLogout();
      }
      if (!response.ok) {
        throw new Error("Failed to upload file");
      }

      const result = await response.json();

      setSnackbar({
        open: true,
        message: result.message || "File uploaded successfully!",
        severity: "success",
      });

      // Refresh the data
      await fetchPartnerInventories();

      setUploadFile(null);
      setShowForm(false);
    } catch (error) {
      console.error("Error uploading file:", error);
      setSnackbar({
        open: true,
        message: error.message || "Failed to upload file",
        severity: "error",
      });
    } finally {
      setUploadLoading(false);
    }
  };

  //   const downloadTemplate = () => {
  //   // Create CSV header row
  //   const header = [
  //     "Stockroom",
  //     "LeaseRefNumber",
  //     "ServiceNowTicket",
  //     "SerialNumber",
  //     "KeyboardType",
  //     "DeviceModel",
  //   ];
  //   const csvContent = header.join(",") + "\n";
  //   const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
  //   saveAs(blob, "PartnerInventoryTemplate.csv");
  // };

  const downloadTemplate = () => {
    const header = [
      [
        "Stockroom",
        "LeaseRefNumber",
        "ServiceNowTicket",
        "SerialNumber",
        "KeyboardType",
        "DeviceModel",
      ],
    ];

    // Create worksheet
    const worksheet = XLSX.utils.aoa_to_sheet(header);

    // Create workbook and add sheet
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Template");

    // Generate Excel file
    const excelBuffer = XLSX.write(workbook, {
      bookType: "xlsx",
      type: "array",
    });
    const blob = new Blob([excelBuffer], { type: "application/vnd.ms-excel" });

    saveAs(blob, "PartnerInventory.xlsx");
  };

  const handleEdit = (item) => {
    setFormData({
      customerAccountId: item.customerAccountId.toString(),
      stockRoom: item.stockRoom,
      leaseRefNumber: item.leaseRefNumber,
      serviceNowTicket: item.serviceNowTicket,
      serialNumber: item.serialNumber,
      keyboardType: item.keyboardType,
      deviceModel: item.deviceModel,
      status: item.status,
    });
    setEditingId(item.id);
    setFormTab(0); // Switch to individual form
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm("Are you sure you want to delete this item?")) {
      try {
        const response = await fetch(`/api/PartnerInventory/${id}`, {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${getToken()}`,
          },
        });

        if (response.status === 401) {
          handleLogout();
        }
        if (!response.ok) {
          throw new Error("Failed to delete partner inventory");
        }

        setSnackbar({
          open: true,
          message: "Partner Inventory deleted successfully!",
          severity: "success",
        });
        await fetchPartnerInventories();
      } catch (error) {
        console.error("Error deleting partner inventory:", error);
        setSnackbar({
          open: true,
          message: error.message || "Failed to delete partner inventory",
          severity: "error",
        });
      }
    }
  };

  const handleCancel = () => {
    setFormData({
      customerAccountId: "",
      stockRoom: "",
      leaseRefNumber: "",
      serviceNowTicket: "",
      serialNumber: "",
      keyboardType: "",
      deviceModel: "",
    });
    setEditingId(null);
    setUploadFile(null);
    setFormTab(0);
    setShowForm(false);
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (
      file &&
      (file.type ===
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" ||
        file.type === "application/vnd.ms-excel")
    ) {
      setUploadFile(file);
    } else {
      setSnackbar({
        open: true,
        message: "Please select a valid Excel file (.xlsx or .xls)",
        severity: "warning",
      });
    }
  };

  // Filter data based on search query
  const filteredData = partnerInventories.filter(
    (item) =>
      item.customerAccountId.toString().includes(searchQuery.toLowerCase()) ||
      item.customer.toLowerCase().includes(searchQuery.toLowerCase()) ||
      item.serialNumber.toLowerCase().includes(searchQuery.toLowerCase()) ||
      item.stockRoom.toLowerCase().includes(searchQuery.toLowerCase()) ||
      item.leaseRefNumber.toLowerCase().includes(searchQuery.toLowerCase()) ||
      item.serviceNowTicket.toLowerCase().includes(searchQuery.toLowerCase()) ||
      item.keyboardType.toLowerCase().includes(searchQuery.toLowerCase()) ||
      item.deviceModel.toLowerCase().includes(searchQuery.toLowerCase()) ||
      item.status.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (!isAuthenticated) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Box sx={{ flexGrow: 1, overflowX: "hidden" }}>
        {/* App Bar */}
        <AppBar position="static" sx={{ backgroundColor: "#1976d2" }}>
          <Toolbar>
            <Box sx={{ display: "flex", alignItems: "center", flexGrow: 1 }}>
              <IconButton
                color="inherit"
                edge="start"
                onClick={() => setNavOpen(true)}
                sx={{ mr: 1 }}
              >
                <MenuIcon />
              </IconButton>
              <img
                src={require("../images/zones_logo_white.png")}
                style={{ height: "20px", marginRight: "15px" }}
                alt="Zones Logo"
              />
              <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
                Partner Inventory Management
              </Typography>
            </Box>

            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              {/* <Tooltip title="Logout"> */}
              <IconButton
                color="inherit"
                onClick={handleLogout}
                sx={{ ml: 1 }}
                title="Logout"
              >
                <LogoutIcon />
              </IconButton>
              {/* </Tooltip> */}
            </Box>
          </Toolbar>
        </AppBar>

        <Drawer
          anchor="left"
          open={navOpen}
          onClose={() => setNavOpen(false)}
          ModalProps={{ keepMounted: true }}
          sx={{
            "& .MuiDrawer-paper": {
              width: 260,
              top: APP_BAR_HEIGHT,
              height: `calc(100% - ${APP_BAR_HEIGHT}px)`,
            },
          }}
        >
          <List>
            <ListItemButton
              component={Link}
              to="/"
              onClick={() => setNavOpen(false)}
            >
              <ListItemIcon>
                <HomeIcon />
              </ListItemIcon>
              <ListItemText primary="Orders" />
            </ListItemButton>
            <ListItemButton
              component={Link}
              to="/partnerInventory"
              onClick={() => setNavOpen(false)}
            >
              <ListItemIcon>
                <InventoryIcon />
              </ListItemIcon>
              <ListItemText primary="Partner Inventory" />
            </ListItemButton>
          </List>
        </Drawer>

        {/* Main Content */}
        <Container maxWidth="xl" sx={{ mt: 3, mb: 3 }}>
          {/* Search and Add Button Section */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 2,
            }}
          >
            <Box
              sx={{ display: "flex", alignItems: "center", gap: 2, flex: 1 }}
            >
              <TextField
                placeholder="Search"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <SearchIcon sx={{ color: "text.secondary", mr: 1 }} />
                  ),
                }}
                sx={{
                  minWidth: 300,
                  "& .MuiOutlinedInput-root": {
                    fontSize: "1rem",
                  },
                }}
                variant="outlined"
                size="small"
              />
            </Box>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setShowForm(true)}
              sx={{
                backgroundColor: "#1976d2",
                "&:hover": { backgroundColor: "#1565c0" },
                textTransform: "none",
              }}
            >
              Add Inventory
            </Button>
          </Box>

          <Paper sx={{ height: "78vh", width: "100%", overflow: "auto" }}>
            {loading ? (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  height: "100%",
                }}
              >
                <CircularProgress />
              </Box>
            ) : (
              <DataGrid
                rows={filteredData}
                columns={columns}
                rowCount={totalCount}
                pagination
                paginationMode="server"
                loading={loading}
                paginationModel={paginationModel}
                onPaginationModelChange={setPaginationModel}
                pageSizeOptions={[10, 25, 50]}
                checkboxSelection={false}
                disableSelectionOnClick
                sx={{
                  "& .MuiDataGrid-cell:focus": {
                    outline: "none",
                  },
                  "& .MuiDataGrid-row:hover": {
                    backgroundColor: "#f5f5f5",
                  },
                }}
              />
            )}
          </Paper>
        </Container>

        {/* Add/Edit Dialog */}
        <Dialog open={showForm} onClose={handleCancel} maxWidth="md" fullWidth>
          <DialogTitle>
            {editingId ? "Edit Partner Inventory" : "Add New Partner Inventory"}
          </DialogTitle>
          <DialogContent>
            <Tabs
              value={formTab}
              onChange={(e, newValue) => setFormTab(newValue)}
              sx={{ mb: 2 }}
            >
              <Tab label="Individual Entry" />
              <Tab label="Upload Excel File" />
            </Tabs>

            {formTab === 0 ? (
              <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2 }}>
                <Stack spacing={3}>
                  <Stack direction={{ xs: "column", sm: "row" }} spacing={3}>
                    {/* <TextField
                      fullWidth
                      label="Customer Account ID"
                      name="customerAccountId"
                      type="number"
                      value={formData.customerAccountId}
                      onChange={handleInputChange}
                      required
                      variant="outlined"
                    /> */}
                    <FormControl fullWidth required variant="outlined">
                      <InputLabel id="customerAccountId-label">
                        Customer Account
                      </InputLabel>
                      <Select
                        labelId="customerAccountId-label"
                        label="Customer Account"
                        name="customerAccountId"
                        value={formData.customerAccountId}
                        onChange={handleInputChange}
                      >
                        {customerAccounts.map((acc) => (
                          <MenuItem key={acc.value} value={acc.value}>
                            {acc.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <FormControl fullWidth required variant="outlined">
                      <InputLabel id="stockRoom-label">Stockroom</InputLabel>
                      <Select
                        labelId="stockRoom-label"
                        label="Stockroom"
                        name="stockRoom"
                        value={formData.stockRoom}
                        onChange={handleInputChange}
                      >
                        {stockRooms.map((room) => (
                          <MenuItem key={room.value} value={room.value}>
                            {room.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Stack>

                  <Stack direction={{ xs: "column", sm: "row" }} spacing={3}>
                    <TextField
                      fullWidth
                      label="Lease Reference Number"
                      name="leaseRefNumber"
                      value={formData.leaseRefNumber}
                      onChange={handleInputChange}
                      required
                      variant="outlined"
                    />
                    <TextField
                      fullWidth
                      label="ServiceNow Ticket"
                      name="serviceNowTicket"
                      value={formData.serviceNowTicket}
                      onChange={handleInputChange}
                      required
                      variant="outlined"
                    />
                  </Stack>

                  <Stack direction={{ xs: "column", sm: "row" }} spacing={3}>
                    <TextField
                      fullWidth
                      label="Serial Number"
                      name="serialNumber"
                      value={formData.serialNumber}
                      onChange={handleInputChange}
                      required
                      variant="outlined"
                      // disabled={Boolean(editingId)}
                    />
                    <FormControl fullWidth required variant="outlined">
                      <InputLabel id="keyboardType-label">Keyboard Type</InputLabel>
                      <Select
                        labelId="keyboardType-label"
                        label="Keyboard Type"
                        name="keyboardType"
                        value={formData.keyboardType}
                        onChange={handleInputChange}
                      >
                        {keyboardTypes.map((type) => (
                          <MenuItem key={type.value} value={type.value}>
                            {type.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Stack>

                  <Stack direction={{ xs: "column", sm: "row" }} spacing={3}>
                    <FormControl fullWidth required variant="outlined">
                      <InputLabel id="deviceModel-label">DeviceModel</InputLabel>
                      <Select
                        labelId="deviceModel-label"
                        label="DeviceModel"
                        name="deviceModel"
                        value={formData.deviceModel}
                        onChange={handleInputChange}
                      >
                        {deviceModels.map((model) => (
                          <MenuItem key={model.value} value={model.value}>
                            {model.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    {editingId && (
                      <FormControl fullWidth required variant="outlined">
                        <InputLabel id="status-label">Status</InputLabel>
                        <Select
                          labelId="status-label"
                          label="Status"
                          name="status"
                          value={formData.status || ""}
                          onChange={handleInputChange}
                        >
                          <MenuItem value="Sent">Sent</MenuItem>
                          <MenuItem value="Waiting">Waiting</MenuItem>
                          <MenuItem value="Error">Error</MenuItem>
                        </Select>
                      </FormControl>
                    )}
                  </Stack>
                </Stack>
              </Box>
            ) : (
              // Upload Form
              <Box sx={{ mt: 2 }}>
                <Box sx={{ mb: 3 }}>
                  <FormControl
                    fullWidth
                    required
                    variant="outlined"
                    sx={{ mb: 2, width: "50%" }}
                  >
                    <InputLabel id="upload-customerAccountId-label">
                      Customer Account
                    </InputLabel>
                    <Select
                      labelId="upload-customerAccountId-label"
                      label="Customer Account"
                      name="customerAccountId"
                      value={formData.customerAccountId}
                      onChange={handleInputChange}
                    >
                      {customerAccounts.map((acc) => (
                        <MenuItem key={acc.value} value={acc.value}>
                          {acc.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 2 }}
                  >
                    Upload an Excel file (.xlsx or .xls) with partner inventory
                    data.
                  </Typography>
                  <Button
                    variant="outlined"
                    component="label"
                    startIcon={<UploadIcon />}
                    sx={{ mb: 2 }}
                  >
                    <input
                      type="file"
                      hidden
                      accept=".xlsx,.xls"
                      onChange={handleFileChange}
                    />
                  </Button>
                  {uploadFile && (
                    <Typography variant="body2" sx={{ mt: 1 }}>
                      Selected file: {uploadFile.name}
                    </Typography>
                  )}
                </Box>
              </Box>
            )}
          </DialogContent>
          <DialogActions sx={{ p: 3, justifyContent: "space-between" }}>
            <Box sx={{ display: "flex", justifyContent: "flex-start", gap: 1 }}>
              {formTab === 1 && (
                <Button
                  variant="outlined"
                  startIcon={<DownloadIcon />}
                  onClick={downloadTemplate}
                  sx={{ mr: 2 }}
                >
                  Download Template
                </Button>
              )}
            </Box>
            <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
              <Button onClick={handleCancel} variant="outlined">
                Cancel
              </Button>

              {formTab === 0 ? (
                <Button
                  onClick={handleSubmit}
                  variant="contained"
                  disabled={loading}
                  startIcon={
                    loading ? (
                      <CircularProgress size={20} color="inherit" />
                    ) : null
                  }
                  sx={{
                    backgroundColor: "#1976d2",
                    "&:hover": { backgroundColor: "#1565c0" },
                  }}
                >
                  {editingId ? "Update" : "Save and Add more"}
                </Button>
              ) : (
                <Button
                  onClick={handleUpload}
                  variant="contained"
                  disabled={
                    !uploadFile || uploadLoading || !formData.customerAccountId
                  }
                  startIcon={
                    uploadLoading ? (
                      <CircularProgress size={20} />
                    ) : (
                      <UploadIcon />
                    )
                  }
                  sx={{
                    backgroundColor: "#1976d2",
                    "&:hover": { backgroundColor: "#1565c0" },
                  }}
                >
                  {uploadLoading ? "Uploading..." : "Upload File"}
                </Button>
              )}
            </Box>
          </DialogActions>
        </Dialog>

        {/* Snackbar for notifications */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={handleCloseSnackbar}
          anchorOrigin={{ vertical: "top", horizontal: "right" }}
        >
          <Alert
            onClose={handleCloseSnackbar}
            severity={snackbar.severity}
            sx={{ width: "100%" }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>
    </ThemeProvider>
  );
};

export default PartnerInventory;
